import {useTheme, css, Typography} from '@mui/material';
import {DlMuiDynamicIcon, DlMuiPopper, GetStylesFunction, useMuiAnchor} from '@wallaby-ui/core';
import {ICellRendererParams} from 'ag-grid-community';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {SerializedStyles} from '@emotion/utils';
import classNames from 'classnames';

type Props = ICellRendererParams & {
  tooltipOverride?: string | number | JSX.Element;
  icon?: {
    iconName: string;
    useFixedViewBox?: string | number | boolean;
    style?: React.CSSProperties;
  };
  style?: React.CSSProperties;
  valueStyle?: React.CSSProperties;
  popperStyle?: SerializedStyles;
  textStyle?: React.CSSProperties;
  bold?: boolean;
  singleLine?: boolean;
  route?: string;
  replaceRoute?: boolean;
  textAlign?: 'right' | 'left';
  alignCenter?: boolean;
  isTooltipInNavigationMenu?: boolean;
  isNavigationOpen?: boolean;
};

export const JustTooltipFormatter: React.FC<Props> = (props) => {
  const {
    value,
    tooltipOverride,
    icon,
    style,
    popperStyle,
    textStyle: _textStyle,
    bold,
    singleLine,
    route,
    replaceRoute,
    textAlign,
    alignCenter,
    valueStyle = {},
    isTooltipInNavigationMenu,
    isNavigationOpen,
  } = props;

  const tooltip = tooltipOverride ?? value;
  const hasTooltip = tooltip != null && tooltip !== '';
  const tooltipContent: string | JSX.Element = React.isValidElement(tooltip) ? tooltip : String(tooltip);
  const theme = useTheme();
  const muiStyles = getStyles({theme});

  const {mouseEventEnter, mouseEventLeave, open, anchorEl} = useMuiAnchor(hasTooltip);

  const wrapperStyle: React.CSSProperties = {
    ...(style ?? {}),
    ...(textAlign ? {textAlign} : {}),
  };
  const textStyle: React.CSSProperties = {
    ...(singleLine ? {whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'} : {}),
    ...(_textStyle ?? {}),
  };
  return (
    <Typography
      variant={bold ? 'valueBold' : 'value'}
      style={{
        maxWidth: '100%',
        ...(alignCenter
          ? {
              height: '100%',
              display: 'inline-flex',
              alignItems: 'center',
            }
          : {}),
        ...wrapperStyle,
      }}
    >
      {route ? (
        <span css={muiStyles.linkWrapper} onMouseEnter={mouseEventEnter} onMouseLeave={mouseEventLeave}>
          <Link css={muiStyles.link} tabIndex={0} to={route} replace={replaceRoute}>
            {icon && (
              <DlMuiDynamicIcon
                iconName={icon.iconName}
                useFixedViewBox={icon.useFixedViewBox}
                css={muiStyles.iconWrapper}
                svgIconInputCss={muiStyles.icon}
                svgIconStyle={icon.style}
              />
            )}
            <span css={muiStyles.linkSpan} style={textStyle}>
              {value}
            </span>
          </Link>
        </span>
      ) : (
        <span
          css={muiStyles.span}
          onMouseEnter={mouseEventEnter}
          onMouseLeave={mouseEventLeave}
          style={{
            ...textStyle,
            ...valueStyle,
          }}
          className={classNames({
            spanNavigationMenu: isTooltipInNavigationMenu,
          })}
        >
          {value}
        </span>
      )}
      <DlMuiPopper
        hasTooltip={hasTooltip}
        anchorEl={anchorEl}
        open={open}
        inputCss={popperStyle}
        isTooltipInNavigationMenu={isTooltipInNavigationMenu}
        isNavigationOpen={isNavigationOpen}
      >
        <Typography variant={'popper'}>{tooltipContent}</Typography>
      </DlMuiPopper>
    </Typography>
  );
};
JustTooltipFormatter.displayName = 'JustTooltipFormatter';

const getStyles: GetStylesFunction = (options) => ({
  linkWrapper: css`
    display: inline-block;
    max-width: 100%;
    color: var(--dlHighlightedColor);
  `,
  link: css`
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    text-decoration: underline;
    font-family: SourceSansPro-Semibold;
    color: var(--dlHighlightedColor);
    cursor: pointer;
    &:hover,
    &:focus {
      outline: none;
      color: var(--dlHighlightedFocusColor);
    }
    &:active {
      outline: none;
      color: var(--dlHighlightedActiveColor);
    }
    padding: ${options?.theme?.spacing(1, 0)};
  `,
  iconWrapper: css`
    padding: 1px 10px 0px 0;
    cursor: pointer !important;
  `,
  icon: css`
    font-size: 13px;
    color: var(--dlTheme-700);
    cursor: pointer;
  `,
  linkSpan: css`
    display: inline-block;
    max-width: 100%;
  `,
  span: css`
    display: inline-block;
    max-width: 100%;
    padding: ${options?.theme?.spacing(1, 0)};

    &.spanNavigationMenu {
      width: 100%;
    }
  `,
});
